import React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import { ThemeProvider } from '@mui/material/styles';

import BrandProvider, { BrandContext } from '../../../contexts/branding';
import { LocaleContext } from '../../../contexts/language';
import { CopyRightBlock } from '../../atoms/copyrightBlock';

import { Link } from 'gatsby-theme-material-ui';
import PropTypes from 'prop-types';
import { DrawerContent as DrawerContent } from '../../atoms/DrawerContent';
import LazyLoad from 'react-lazyload';
import DNBreadcrumbs from '../../molecules/DNBreadcrumbs';
import AppBar from '../../atoms/AppBar';

const NewsLetterBlock = React.lazy(() => import('../../molecules/NewsletterBlock'));
const FooterMenuBlock = React.lazy(() => import('../../molecules/FooterMenuBlock'));
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const DriveNinjaTemplate = ({
  children, drawerMenuItems, headerMenuItems, userProfile,
  footerMenus, breadcrumbs, pagination, siteHeaderImage, headerHero
}) => {
  const brandConfig = React.useContext(BrandContext);
  const localeConfig = React.useContext(LocaleContext);
  const theme = { ...brandConfig.muiTheme } || {};
  theme.direction = localeConfig.direction;
  const container = typeof window !== undefined ? () => window.document.body : undefined;
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <React.StrictMode>
      <div dir={localeConfig.direction}>
        <GlobalStyles styles={{ ul: { margin: 0, padding: 0, listStyle: 'none' } }} />
        <CssBaseline />
        <ThemeProvider theme={theme}>
          <BrandProvider brandConfig={brandConfig}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <AppBar
                closeUrl={brandConfig?.layout?.appBar?.closeUrl}
                siteHeaderImage={siteHeaderImage}
                onToggleDrawer={handleDrawerToggle}
                headerMenuItems={headerMenuItems}
                userProfile={userProfile}
              />
              <Box component="nav">
                <Drawer
                  container={container}
                  variant="temporary"
                  open={drawerOpen}
                  onClose={handleDrawerToggle}
                  ModalProps={{
                    keepMounted: true // Better open performance on mobile.
                  }}
                  PaperProps={{
                    sx: {
                      backgroundColor: '#ffffff'
                    }
                  }}
                  sx={{
                    display: { xs: 'block', lg: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 }
                  }}
                >
                  <DrawerContent onToggle={handleDrawerToggle} drawerMenuItems={drawerMenuItems}
                    siteHeaderImage={siteHeaderImage} />
                </Drawer>
              </Box>
              {headerHero}
              {(children || breadcrumbs || pagination) ? (
                <Container>
                  <React.Suspense fallback={null}>
                    {breadcrumbs ? (
                      <DNBreadcrumbs paths={breadcrumbs} />
                    ) : null}
                  </React.Suspense>

                  {children}

                  {pagination ? (

                    <Stack
                      alignItems="center"
                      spacing={2}
                      sx={{ mb: 3, mt: 3 }}
                    >
                      <Pagination
                        count={pagination.numberOfPages}
                        // size="large"
                        color="primary"
                        shape="rounded"
                        variant={'outlined'}
                        showFirstButton
                        showLastButton
                        page={pagination.humanPageNumber}
                        siblingCount={1} boundaryCount={0}
                        renderItem={(item) => {
                          const { page } = item;
                          return page > 1 ? (
                            <Link
                              underline="none"
                              to={item.disabled ? '#' : (pagination.getUrl ? pagination.getUrl({ page }) : `${pagination.pathPrefix}/${page}`)}
                            >
                              <PaginationItem {...item} />
                            </Link>
                          ) : (
                            <Link underline="none"
                              to={pagination.getUrl ? pagination.getUrl({ page }) : pagination.pathPrefix}><PaginationItem {...item} /></Link>
                          );
                        }}
                      />
                    </Stack>

                  ) : null}
                </Container>
              ) : null}
              <LazyLoad height={690}>
                <React.Suspense fallback={<div style={{ height: 690 }} />}>
                  <NewsLetterBlock />
                </React.Suspense>
              </LazyLoad>
              {brandConfig?.layout?.footer?.footerMenu?.display ? (
                <LazyLoad height={690}>
                  <React.Suspense fallback={<div style={{ height: 690 }} />}>
                    <FooterMenuBlock menus={footerMenus} />
                  </React.Suspense>
                </LazyLoad>
              ):null}
              <CopyRightBlock />
            </LocalizationProvider>
          </BrandProvider>
        </ThemeProvider>
      </div>
    </React.StrictMode>
  );
};
DriveNinjaTemplate.defaultProps = {
  headerHero: null
};
DriveNinjaTemplate.propTypes = {
  headerHero: PropTypes.node,
  children: PropTypes.node,
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired
  })),
  pagination: PropTypes.shape({
    getUrl: PropTypes.func,
    pathPrefix: PropTypes.string,
    numberOfPages: PropTypes.number.isRequired,
    humanPageNumber: PropTypes.number.isRequired
  }),
  siteHeaderImage: PropTypes.node,
  headerMenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      linkText: PropTypes.string.isRequired,
      linkTitle: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })
  ),
  drawerMenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      linkText: PropTypes.string.isRequired,
      linkTitle: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })
  ),
  userProfile: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    profilePicture: PropTypes.string.isRequired,
    signOut: PropTypes.func.isRequired,
    userMenu: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      linkText: PropTypes.string.isRequired,
      linkTitle: PropTypes.string.isRequired
    }))
  }),

  footerMenus: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      linkText: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.node]),
      linkTitle: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.node])
    }))
  }))
};

export { DriveNinjaTemplate };